import React, { useState, useCallback, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import {
  Banner,
  Button,
  Checkbox,
  Link,
  FieldBox,
  NativeInput,
} from '@pushwoosh/dumb-components';
import { Color, Tag } from '@pushwoosh/kit-constants';
import { Horizontal } from '@pushwoosh/kit-helpers';
import { Text } from '@pushwoosh/kit-typography';

import { H1 } from '~/src/ui-kit';
import { useEnvironment } from '~/src/contexts/environment';
import { ContentBox } from '~/src/components/ContentBox';
import { ButtonLoader } from '~/src/components/ButtonLoader';
import { useCountdown } from '~/src/hooks/useCountdown';
import { Lang, useLang } from '~/src/polyglot';

import { authOptionName } from '../helpers/authOptionName';
import {
  HeaderBox,
  FormStyled,
  InputFieldsBox,
  ButtonsBox,
} from './styles';
import type { TwoFactorCodeProps } from './TwoFactorCode.types';

export function TwoFactorCode({
  sendCodeHandler,
  confirmHandler,
  errorMessage,
  isTrustedOption,
  authMethod = '',
  authOptions = [],
}: TwoFactorCodeProps): ReactElement {
  const navigate = useNavigate();
  const { waitSeconds, setCountStartDate } = useCountdown(60);
  const { getLinkByName, productName } = useEnvironment();
  const lang = useLang();

  const [isResendSubmitting, setResendSubmitting] = useState(false);

  const isAllowSupport = productName === 'Pushwoosh';
  const isMultiAuthOptions = authOptions?.length > 1;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
  } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      confirmationCode: '',
      isTrusted: false,
    },
  });

  const onSendCodeSubmit = useCallback(() => {
    setResendSubmitting(true);
    sendCodeHandler().then(() => {
      setCountStartDate(Date.now());
      setResendSubmitting(false);
    });
  }, [sendCodeHandler, setCountStartDate, setResendSubmitting]);

  return (
    <>
      <ContentBox>
        <FormStyled onSubmit={handleSubmit(confirmHandler)}>
          <HeaderBox>
            <H1><Lang message="TWO_FACTOR.TITLE" /></H1>
            {authMethod === 'authenticator'
              ? (
                <p>
                  <Lang message="TWO_FACTOR.SUBTITLE.AUTHENTICATOR" />
                </p>
              ) : (
                <p>
                  <Lang message="TWO_FACTOR.SUBTITLE.SENT" method={authOptionName(authMethod, lang)} />
                </p>
              )}
          </HeaderBox>
          <InputFieldsBox>
            <FieldBox
              title={lang({ message: 'TWO_FACTOR.FIELD.CODE' })}
              error={!!errors.confirmationCode && (
                <Text $variant="footnote" $color={Color.DANGER}>
                  <Lang message="TWO_FACTOR.ERROR.CODE" />
                </Text>
              )}
            >
              <NativeInput
                autoFocus
                $isErrored={!!errors.confirmationCode}
                {...register('confirmationCode', { required: true })}
              />
            </FieldBox>
            {isTrustedOption && (
              <Checkbox
                name="is-trusted"
                label={(
                  <span>
                    <Lang message="TWO_FACTOR.TRUST.LABEL" />
                    <br />
                    <Text $variant="footnote" $color={Color.LOCKED}>
                      <Lang message="TWO_FACTOR.TRUST.HINT" />
                    </Text>
                  </span>
                )}
                isChecked={watch('isTrusted')}
                onChange={(isChecked): void => setValue('isTrusted', isChecked)}
              />
            )}
            {!!errorMessage && !isSubmitting && <Banner type="error" title={errorMessage} />}
          </InputFieldsBox>
          <ButtonsBox>
            <Button
              type="submit"
              color="primary"
              isLoading={isSubmitting}
            >
              <Lang message="TWO_FACTOR.BUTTON.CONFIRM" />
            </Button>
            {authMethod !== 'authenticator' && (!waitSeconds ? (
              <Button
                type="button"
                color="primary"
                view="ghost"
                isLoading={isSubmitting || isResendSubmitting}
                onClick={onSendCodeSubmit}
              >
                <Lang message="TWO_FACTOR.BUTTON.RESEND" />
              </Button>
            ) : <ButtonLoader><Lang message="TWO_FACTOR.BUTTON.RESEND_WAIT" seconds={waitSeconds} /></ButtonLoader>
            )}
          </ButtonsBox>
          {(isAllowSupport || isMultiAuthOptions) && (
            <Horizontal $alignItems="center" $justifyContent="center" $gap={4}>
              <Lang message="TWO_FACTOR.SUPPORT.QUESTION" />
              {' '}
              {isMultiAuthOptions
                ? (
                  <Link
                    as={Tag.BUTTON}
                    title={lang({ message: 'TWO_FACTOR.LINK.OTHER_OPTIONS' })}
                    onClick={(): void => navigate(getLinkByName('authOptions'))}
                  >
                    <Lang message="TWO_FACTOR.LINK.OTHER_OPTIONS" />
                  </Link>
                )
                : (
                  <Link
                    title={lang({ message: 'TWO_FACTOR.LINK.CONTACT_SUPPORT' })}
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://help.pushwoosh.com/hc/en-us/requests/new"
                  >
                    <Lang message="TWO_FACTOR.LINK.CONTACT_SUPPORT" />
                  </Link>
                )}
            </Horizontal>
          )}
        </FormStyled>
      </ContentBox>
      <Horizontal $alignItems="center">
        <Link title={lang({ message: 'TWO_FACTOR.LINK.LOG_OUT' })} href={getLinkByName('login')}>
          <Lang message="TWO_FACTOR.LINK.LOG_OUT" />
        </Link>
      </Horizontal>
    </>
  );
}
